/**
 * ============================================================================
 * SignDispositionDialog — Phase 6a frontend (T-P6a-W3-03)
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: confirmation modal for the QA Manager / Doc Controller signature
 *          on a disposition task. Enforces WebAuthn (phishing-resistant 2FA)
 *          on the client by detecting `window.PublicKeyCredential`; if absent
 *          the UI surfaces the "set up 2FA" path instead of letting the user
 *          submit something that the server will reject anyway.
 *
 * REGULATORY:
 *   - DESTROY/ARCHIVE/EXPORT are irreversible, so SMS/TOTP factors are not
 *     accepted (PFI-policy). Only WebAuthn assertions are forwarded; the
 *     signature-svc verifies the assertion and binds it to the audit row.
 *
 * A11Y:
 *   - role="dialog" + aria-modal + aria-labelledby + aria-describedby.
 *   - The "no WebAuthn" envelope is a real <div role="alert"> so screen
 *     readers announce it instead of letting the dialog look "broken".
 * ============================================================================ */

import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import styles from '../AuthorWorkbench/NewDocumentForm.module.css';
import trayStyles from './RecordsDispositionTray.module.css';
import { useSignDispositionMutation } from './useSignDispositionMutation';
import type { DispositionTask, SignerRole } from './useDispositionTasksQuery';

export interface SignDispositionDialogProps {
  task: DispositionTask;
  role: SignerRole;
  onClose: () => void;
  onSigned: () => void;
}

export function SignDispositionDialog({
  task,
  role,
  onClose,
  onSigned,
}: SignDispositionDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const signM = useSignDispositionMutation();

  const webauthnSupported = useMemo(
    () =>
      typeof window !== 'undefined' &&
      typeof window.PublicKeyCredential !== 'undefined',
    [],
  );

  const defaultAttestation = useMemo(() => {
    return role === 'QA_MANAGER'
      ? t('retention.disposition.attestation.qa', {
          defaultValue:
            'I confirm that the listed record is eligible for {{action}} per the active retention policy and that no open CAPA, change control, or legal hold prevents it.',
          action: task.action,
        })
      : t('retention.disposition.attestation.dc', {
          defaultValue:
            'I confirm that the document index has been updated and that retention metadata reflects this {{action}} action.',
          action: task.action,
        });
  }, [role, task.action, t]);

  const [attestationText, setAttestationText] = useState(defaultAttestation);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!webauthnSupported) {
      setError(t('retention.disposition.dialog.webauthnRequired', {
        defaultValue: 'Phishing-resistant 2FA (WebAuthn) is required to sign disposition tasks.',
      }));
      return;
    }
    if (attestationText.trim().length < 10) {
      setError(t('retention.disposition.errors.attestationTooShort', {
        defaultValue: 'Attestation must be at least 10 characters.',
      }));
      return;
    }
    try {
      // Phase 6a: the WebAuthn assertion is stubbed by signature-svc. We
      // pass a deterministic placeholder so the server-side audit row gets
      // a non-null assertion id. The real adapter (T-P7-W?) calls
      // navigator.credentials.get() and forwards the result here.
      const assertionId = `mock-webauthn-${task.task_id}`;
      await signM.mutateAsync({
        taskId: task.task_id,
        role,
        attestationText: attestationText.trim(),
        webauthnAssertionId: assertionId,
      });
      onSigned();
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : t('retention.disposition.errors.serverFailed', {
              defaultValue: 'Server rejected the signature.',
            }),
      );
    }
  }

  const roleLabel = t(`retention.disposition.signers.role.${role}`, {
    defaultValue: role === 'QA_MANAGER' ? 'QA Manager' : 'Doc Controller',
  });

  return (
    <div
      className={styles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {t('retention.disposition.dialog.title', {
              defaultValue: 'Sign as {{role}}',
              role: roleLabel,
            })}
          </h2>
          <p id={descId} className={styles.desc}>
            <code>{task.doc_number}</code>{' '}
            {task.document_title ?? ''} ·{' '}
            {t(`retention.disposition.action.${task.action}.label`, {
              defaultValue: task.action,
            })}
          </p>
        </header>

        <form className={styles.form} onSubmit={onSubmit} noValidate>
          {!webauthnSupported && (
            <div role="alert" className={trayStyles.webauthnEnvelope}>
              <h3 className={trayStyles.webauthnTitle}>
                {t('retention.disposition.dialog.webauthnHeading', {
                  defaultValue: 'Phishing-resistant 2FA required',
                })}
              </h3>
              <p>
                {t('retention.disposition.dialog.webauthnBody', {
                  defaultValue:
                    'This action requires a WebAuthn security key or platform authenticator. Your browser does not advertise WebAuthn support.',
                })}
              </p>
              <p>
                <a
                  href="/2fa/enrollment"
                  className={trayStyles.webauthnLink}
                  aria-label={t('retention.disposition.dialog.webauthnSetupAria', {
                    defaultValue: 'Open the two-factor authentication setup page',
                  })}
                >
                  {t('retention.disposition.dialog.webauthnSetupLink', {
                    defaultValue: 'Set up 2FA →',
                  })}
                </a>
              </p>
            </div>
          )}

          {error && (
            <div role="alert" className={styles.errorBanner}>
              {error}
            </div>
          )}

          <div className={styles.field}>
            <label htmlFor="sd-attestation" className={styles.label}>
              {t('retention.disposition.dialog.attestationLabel', {
                defaultValue: 'Attestation text',
              })}
              <span className={styles.required} aria-hidden="true">*</span>
            </label>
            <textarea
              id="sd-attestation"
              className={styles.input}
              rows={4}
              value={attestationText}
              onChange={(e) => setAttestationText(e.target.value)}
              required
            />
          </div>

          {webauthnSupported && (
            <p className={trayStyles.webauthnHint} role="note">
              {t('retention.disposition.dialog.webauthnHint', {
                defaultValue:
                  'Submitting will prompt your security key or platform authenticator (Touch ID / Windows Hello).',
              })}
            </p>
          )}

          <footer className={styles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={signM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={signM.isPending}
              disabled={!webauthnSupported}
            >
              {t('retention.disposition.dialog.signSubmit', {
                defaultValue: 'Sign with WebAuthn',
              })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
